import React, { useEffect, useState } from "react"
import SearchBar from "./SearchBar"
import TableBody from "./TableBody"
import TableHead from "./TableHead"
import { useSortData } from "./Functions/sortData"
import styled from "styled-components"
import Pagination from "./Pagination"
import { observer } from "mobx-react"
import filterRecords from "./Functions/filterRecords"
import { TableFooter } from "./TableFooter"
import { EditTableCellCreateFn } from "./EditTableRow"
import { useStore } from "../../../../Contexts/GlobalStoreContext"
import { useHandlePagination } from "./Functions/handlePagination"
import { useTranslation } from "react-i18next"
import concatWithSpaces from "components/ProjectNonSpecificComponents/Functions/concatWithSpaces"
import {
	TOnClickTableRow,
	TSelectedTableRowId,
	TTableHeadCell,
} from "components/ProjectSpecificComponents/Interfaces/TableInterface"
import { TableHeader, TableLayout, TBody, Td, Tr } from "./StyledComponents/TableStyleComp"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import "./table.css"
import { TSortConfig } from "./Interfaces/Interfaces"

type TScrollRecords = {
	set: boolean
	bodyHeight: string
}
interface Props {
	head: TTableHeadCell[]
	records: any[] | undefined
	tableRow: (row: any) => React.ReactNode
	editTableCell?: EditTableCellCreateFn
	search?: boolean
	editable?: boolean
	selectedRowId?: TSelectedTableRowId
	gridArea?: string
	containerStyle?: React.CSSProperties
	sortable?: boolean
	tableName?: string
	padding?: string
	clickedOnRow?: TOnClickTableRow
	CustomTableFooter?: React.ReactNode
	AddNewRecordRow?: React.ReactNode
	HeaderExtraElement?: React.ReactNode
	fixedColumnWidth?: boolean
	tableHeadStyle?: React.CSSProperties
	tableRowStyle?: React.CSSProperties
	/**
	 * For Scrolling records
	 */
	scrollRecords?: TScrollRecords
	/**
	 * For default sort order
	 */
	defaultSortOrder?: TSortConfig
}

export const Table = observer(
	({
		head,
		padding,
		tableRow,
		records = [],
		editTableCell,
		search = false,
		editable = false,
		selectedRowId,
		gridArea,
		containerStyle = {},
		sortable,
		tableName,
		clickedOnRow,
		CustomTableFooter,
		AddNewRecordRow,
		HeaderExtraElement,
		fixedColumnWidth = false,
		tableHeadStyle,
		tableRowStyle,
		scrollRecords = {
			set: false,
			bodyHeight: "",
		},
		defaultSortOrder,
	}: Props) => {
		const store = useStore()
		const { t } = useTranslation()
		const [searchText, setSearchText] = useState<string>("")

		const getSortableColumns = () => {
			const sortableColumns: string[] = new Array()
			head.forEach(({ key, searchable }) => searchable && sortableColumns.push(key))
			return sortableColumns
		}

		const { items: unfilteredItems, requestSort, sortConfig, setSortConfig } = useSortData(records)
		const filteredItems = filterRecords(unfilteredItems, searchText, getSortableColumns())

		const {
			onePageData,
			totalItemCount,
			itemsPerPage,
			changeRowsPerPage,
			currentPage,
			paginate,
			pageCount,
		} = useHandlePagination(filteredItems)

		useEffect(() => {
			if (defaultSortOrder) {
				setSortConfig({
					key: defaultSortOrder?.key,
					direction: defaultSortOrder?.direction,
					type: defaultSortOrder?.type,
				})
			} else {
				requestSort(head[0]?.key, head[0]?.type ?? "")
			}
		}, [defaultSortOrder])

		const getClassNamesFor = (name: string) => {
			if (!sortConfig) return
			return sortConfig.key === name ? sortConfig.direction : undefined
		}

		useEffect(() => {
			paginate(1)
		}, [store.triggerGoToFirstPage, searchText])

		return (
			<TableComponent
				style={{
					gridArea,
					padding,
					...containerStyle,
				}}
			>
				<HyperDiv spacedBetween padding="0 .625rem">
					{search && (
						<SearchBar
							tooltipText={t("table.tooltip-search")}
							placeholder={t("forms.field-label.search")}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							searchStyle={{
								borderRadius: 5,
							}}
						/>
					)}
					<HyperDiv direction="row" spaceBetweenElems="1rem" alignedRight centeredY>
						{HeaderExtraElement}
					</HyperDiv>
				</HyperDiv>

				<Whitespace x="1.225" />

				{tableName && (
					<TableHeader className="center-y">
						<div className="font-bold">{tableName}</div>
					</TableHeader>
				)}

				<TableLayout className={concatWithSpaces(scrollRecords.set ? "scroll-records" : "")}>
					<TableHead
						head={head}
						requestSort={requestSort}
						getClassNamesFor={getClassNamesFor}
						sortable={sortable}
						tableHeadStyle={tableHeadStyle}
					/>

					{onePageData.length === 0 && (
						<TBody>
							<Tr>
								<Td colSpan={head.length} style={{ height: 100 }}>
									<label className="center-x-y" style={{ height: "100%" }}>
										{t("table.noData")}
									</label>
								</Td>
							</Tr>
						</TBody>
					)}

					{onePageData.length > 0 && (
						<TableBody
							head={head}
							data={onePageData}
							editTableCell={editTableCell}
							tableRow={tableRow}
							editable={editable}
							clickedOnRow={clickedOnRow}
							currentPage={currentPage}
							AddNewRecordRow={AddNewRecordRow}
							tableRowStyle={tableRowStyle}
							bodyHeight={scrollRecords.bodyHeight ?? ""}
						/>
					)}

					{CustomTableFooter ? CustomTableFooter : <TableFooter data={records} head={head} />}
				</TableLayout>

				<Pagination
					currentPage={currentPage}
					changeRowsPerPage={changeRowsPerPage}
					rowsPerPage={itemsPerPage}
					totalRowCount={totalItemCount}
					paginate={paginate}
					pageCount={pageCount}
				/>
			</TableComponent>
		)
	}
)

export default Table

export const TableComponent = styled.div`
	width: 100%;
	padding: 15px 2px;
	overflow-x: auto;
	height: 100%;
	overflow-y: auto;
`
